// File: src/pages/ShopWithAI.jsx
// Premium AI Shopping Assistant - Tunisia Edition
// Elegant Light Theme - Red & White Only

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { aiSearchAPI } from '../services/api';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { 
  FiLoader, 
  FiCpu, 
  FiAlertCircle, 
  FiSend,
  FiShoppingBag,
  FiTrash2,
  FiMessageCircle,
  FiGlobe,
  FiZap,
  FiTarget,
  FiSearch,
  FiChevronRight
} from 'react-icons/fi';
import './css/ShopWithAI.css';

const ShopWithAI = ({ openCart }) => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const restoringRef = useRef(false);
  const hasInteractedRef = useRef(false);
  const STORAGE_KEY = 'aiShopMessages';
  const siteUrl = process.env.REACT_APP_SITE_URL || 'http://localhost:3000';
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: "Assistant d'achat IA Reyza",
    description: 'Assistant shopping IA bilingue (Darija/francais/anglais) pour decouvrir les meilleurs produits en Tunisie.',
    url: `${siteUrl}/shop-ai`,
    applicationCategory: 'Shopping',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'TND'
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/shop-ai?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  // Load messages from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem(STORAGE_KEY);
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        const validMessages = parsed
          .filter(msg => msg.timestamp && Date.now() - new Date(msg.timestamp).getTime() < 24 * 60 * 60 * 1000)
          .slice(-50);
        if (validMessages.length > 0) {
          restoringRef.current = true;
        }
        setMessages(validMessages);
      } catch (e) {
        console.warn('Failed to load saved messages:', e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Save messages to localStorage on change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (restoringRef.current) {
      restoringRef.current = false;
      return;
    }
    if (!hasInteractedRef.current) {
      return;
    }
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const detectLanguage = (text) => {
    if (!text || typeof text !== 'string') return 'mixed';
    
    const lowerText = text.toLowerCase();
    const hasArabic = /[\u0600-\u06FF]/.test(text);
    const hasFrench = /[àâäéèêëïîôùûüÿæœç]/i.test(text);
    const hasDigitsFranco = /[0-9]/.test(text) && /[a-z]/i.test(text) && !hasArabic;
    const DARIJA_KEYWORDS = [
      'nheb', 'n7eb', 'nhebch', 'ala', '3la', 'brsha', 'brch', 'telij', 'telifoun',
      'cha7n', 'chargeour', 'ordi', 'hassoub', 'pascher', 'rkhiss', 'soma', 'dinara',
      'yalla', 'shnuwa', 'kifkif', 'labas', 'chbik', 'wesh', 'tounsi', 'tounes',
      'darja', 'franco', 'mix', 'wallah', 'inshallah', 'bismillah'
    ];
    const hasDarijaKeywords = DARIJA_KEYWORDS.some(keyword => lowerText.includes(keyword));
    const hasEnglish = /[a-z]/i.test(text) && !hasFrench && !hasDarijaKeywords;

    if (hasArabic) return 'arabic';
    if (hasDarijaKeywords || hasDigitsFranco) return 'tunisian';
    if (hasFrench) return 'french';
    if (hasEnglish) return 'english';
    return 'mixed';
  };

  const getProductsHeader = (count, lang) => {
    if (lang === 'arabic' || lang === 'tunisian') {
      return `${count} منتج${count !== 1 ? 'ات' : ''}`;
    } else if (lang === 'french') {
      return `${count} produit${count !== 1 ? 's' : ''}`;
    }
    return `${count} product${count !== 1 ? 's' : ''}`;
  };

  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
    setQuery('');
    inputRef.current?.focus();
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    const trimmedQuery = query.trim();
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setError('Veuillez saisir au moins 2 caracteres');
      return;
    }

    hasInteractedRef.current = true;
    const userMessage = {
      type: 'user',
      text: trimmedQuery,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);
    setError(null);

    try {
      const { data } = await aiSearchAPI.search(trimmedQuery);
      
      const aiMessage = {
        type: 'ai',
        text: data.conversationalResponse || data.message,
        products: data.products || [],
        productRecommendations: data.productRecommendations || [],
        suggestions: data.suggestions || [],
        intent: data.intent,
        semanticExpansion: data.semanticExpansion,
        retryInfo: data.retryInfo,
        detectedLang: data.understood?.detectedLanguage || detectLanguage(trimmedQuery) || 'french',
        responseTime: data.responseTime,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
    } catch (err) {
      console.error('AI search error:', err);
      const errorText = err.response?.data?.conversationalResponse || 
                        err.response?.data?.message || 'Une erreur est survenue. Veuillez reessayer.';
      const errorMessage = {
        type: 'error',
        text: errorText,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    setTimeout(() => handleSearch(), 0);
  };

  const handleExampleClick = (example) => {
    setQuery(example);
    inputRef.current?.focus();
  };

  const exampleQueries = [
    'nheb chargeour Samsung',
    'نحب تلفزة كبيرة',
    'Ordinateur portable pas cher',
    'telifoun 5G rapide',
    'cha7nin iPhone',
    'Chaussures rouges moins de 200'
  ];

  const features = [
  { icon: <FiGlobe />, title: 'Multilingue', desc: 'Arabe, francais, anglais' },
  { icon: <FiCpu />, title: "Propulse par l'IA", desc: 'IA avancee' },
  { icon: <FiZap />, title: 'Rapide', desc: 'Resultats immediats' },
  { icon: <FiTarget />, title: 'Precise', desc: 'Grande precision' }
];

  const showHero = messages.length === 0;
  const showClearButton = messages.length > 0;

  return (
    <>
      <SEO
        title="Acheter avec l'IA | Assistant d'achat Reyza"
        description="Posez vos questions en Darija, français ou anglais et laissez l'IA trouver les meilleurs produits Reyza pour vous."
        keywords="IA shopping Tunisie, assistant d'achat, rechercher produits, Reyza AI"
        url="/shop-ai"
        type="website"
        structuredData={seoStructuredData}
        breadcrumbs={[
          { name: 'Accueil', url: '/' },
          { name: "Acheter avec l'IA", url: '/shop-ai' }
        ]}
      />

      <div className="shop-ai-page">
      <div className="shop-ai-container">
        
        {/* Hero Section */}
        {showHero && (
          <>
            <section className="hero-section">
              <div className="hero-decoration" />
              
              <div className="hero-content">
                {/* Badge */}
                <div className="hero-badge">
                  <span className="hero-badge-indicator" />
                  <span className="hero-badge-text">Propulse par l'IA - Tunisie</span>
                </div>
                
                {/* Icon */}
                <div className="hero-icon-wrapper">
                  <div className="hero-icon-bg" />
                  <div className="hero-icon-ring" />
                  <div className="hero-icon">
                    <FiCpu />
                  </div>
                </div>
                
                {/* Title */}
                <h1 className="hero-title">
                  Acheter avec <span className="hero-title-accent">IA</span>
                </h1>
                
                {/* Subtitle */}
                <p className="hero-subtitle">
                  <span className="hero-subtitle-arabic">تحدث بالعربية، الفرنسية أو الإنجليزية</span>
                  Votre assistant d'achat intelligent comprend tout
                </p>
              </div>
            </section>

            {/* Features Grid */}
            <section className="features-section">
              <div className="features-grid">
                {features.map((feature, index) => (
                  <div key={index} className="feature-card">
                    <div className="feature-icon-box">{feature.icon}</div>
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-desc">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Examples */}
            <section className="examples-section">
              <div className="examples-header">
                <div className="examples-icon">
                  <FiMessageCircle />
                </div>
                  <span className="examples-label">Essayez ces exemples :</span>
              </div>
              <div className="examples-chips">
                {exampleQueries.map((example, index) => (
                  <button
                    key={index}
                    className="example-chip"
                    onClick={() => handleExampleClick(example)}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Clear Chat Button */}
        {showClearButton && (
          <button className="clear-chat-btn" onClick={clearChat} aria-label="Effacer la conversation">
            <FiTrash2 />
            <span className="btn-text">Effacer</span>
          </button>
        )}

        {/* Chat Messages */}
        {messages.length > 0 && (
          <div className="chat-wrapper">
            <div className="chat-messages">
              {messages.map((message, index) => (
                <div key={index} className={`message message-${message.type}`}>
                  
                  {/* User Message */}
                  {message.type === 'user' && (
                    <div className="user-bubble">
                      <p>{message.text}</p>
                    </div>
                  )}

                  {/* AI Message */}
                  {message.type === 'ai' && (
                    <>
                      <div className="ai-avatar">
                        <FiCpu />
                      </div>
                      <div className="ai-content">
                        {/* Response Bubble */}
                        <div className="ai-bubble">
                          <p>{message.text}</p>
                          {message.responseTime && (
                            <span className="response-time">
                              ⚡ {message.responseTime}ms
                            </span>
                          )}
                        </div>

                        {/* Intent Tags */}
                        {message.intent && (
                          <div className="intent-box">
                            <span className="intent-header">Intention detectee</span>
                            <div className="intent-tags">
                              {message.intent.keywords?.map((keyword, i) => (
                                <span key={i} className="intent-tag keyword">
                                  {keyword}
                                </span>
                              ))}
                              {message.intent.category && (
                                <span className="intent-tag category">
                                  📂 {message.intent.category}
                                </span>
                              )}
                              {message.intent.brand && (
                                <span className="intent-tag brand">
                                  🏷️ {message.intent.brand}
                                </span>
                              )}
                              {message.intent.priceRange && (
                                <span className="intent-tag price">
                                  💰 {message.intent.priceRange.min || 0} - {message.intent.priceRange.max || '∞'} DT
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Products Section - Horizontal Scroll */}
                        {message.products && message.products.length > 0 && (
                          <div className="products-section">
                            <div className="products-header">
                              <div className="products-header-left">
                                <div className="products-icon">
                                  <FiShoppingBag />
                                </div>
                                <div>
                                  <span className="products-title">Produits trouves</span>
                                  <span className="products-count">
                                    {getProductsHeader(message.products.length, message.detectedLang)}
                                  </span>
                                </div>
                              </div>
                              <div className="products-scroll-hint">
                                <span>Defiler</span>
                                <FiChevronRight />
                              </div>
                            </div>
                            
                            <div className="products-carousel">
                              <div className="products-track">
                                {message.products.map((product, prodIndex) => {
                                  const rec = message.productRecommendations?.find(r => r.productId == product.id);
                                  return (
                                    <div key={`${product.id}-${prodIndex}`} className="product-card-wrapper">
                                      <ProductCard product={product} openCart={openCart} />
                                      {rec && (
                                        <div className="product-rec-badge">{rec.message}</div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Suggestions */}
                        {(!message.products || message.products.length === 0) && message.suggestions?.length > 0 && (
                          <div className="suggestions-box">
                            <p className="suggestions-label">
                              {message.detectedLang === 'tunisian' ? 'جرّب هاذم:' :
                               message.detectedLang === 'french' ? 'Essayez :' : 'Essayez :'}
                            </p>
                            <div className="suggestions-chips">
                              {message.suggestions.map((suggestion, i) => (
                                <button
                                  key={i}
                                  className="suggestion-chip"
                                  onClick={() => handleSuggestionClick(suggestion)}
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Error Message */}
                  {message.type === 'error' && (
                    <>
                      <div className="error-avatar">
                        <FiAlertCircle />
                      </div>
                      <div className="error-bubble">
                        <p>{message.text}</p>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {/* Loading State */}
              {loading && (
                <div className="message message-ai">
                  <div className="ai-avatar">
                    <FiCpu />
                  </div>
                  <div className="typing-container">
                    <div className="typing-bubble">
                      <div className="typing-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <p className="typing-text">AI يحلل طلبك...</p>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Fixed Search Bar */}
      <div className="search-bar-fixed">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-container">
            <FiSearch className="search-prefix-icon" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="اكتب هنا... / Tapez ici... / Ecrivez ici..."
              className="search-input"
              disabled={loading}
              maxLength={500}
              aria-label="Rechercher des produits"
            />
              <button
                type="submit"
                className="search-submit-btn"
                disabled={loading || !query.trim() || query.trim().length < 2}
                aria-label="Rechercher"
              >
                {loading ? (
                  <FiLoader className="icon-spin" size={34} style={{ width: '34px', height: '34px' }} />
                ) : (
                  <FiSend size={48} style={{ width: '48px', height: '48px' }} />
                )}
              </button>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default ShopWithAI;
